import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Leaf, ArrowLeft, Trophy, Coins, Timer, Lightning, Play, X } from '@phosphor-icons/react';

const QUESTIONS = [
    { question: "What do koalas mainly eat?", options: ["Grass", "Eucalyptus leaves", "Insects", "Fish"], answer: 1 },
    { question: "Which Australian animal lays eggs but is a mammal?", options: ["Kangaroo", "Wombat", "Platypus", "Dingo"], answer: 2 },
    { question: "What is a baby kangaroo called?", options: ["Cub", "Pup", "Joey", "Kit"], answer: 2 },
    { question: "Which animal is known as the world's happiest animal?", options: ["Koala", "Quokka", "Wombat", "Bilby"], answer: 1 },
    { question: "What shape is a wombat's poo?", options: ["Round", "Cube", "Oval", "Triangle"], answer: 1 },
    { question: "Where is the Great Barrier Reef located?", options: ["Western Australia", "Tasmania", "Queensland", "Victoria"], answer: 2 },
    { question: "Which Australian bird can't fly?", options: ["Kookaburra", "Cockatoo", "Emu", "Lorikeet"], answer: 2 },
    { question: "How many hours a day can a koala sleep?", options: ["8", "14", "18", "22"], answer: 3 },
    { question: "What does an echidna eat?", options: ["Leaves", "Ants and termites", "Fish", "Fruit"], answer: 1 },
    { question: "Which bird's call sounds like laughter?", options: ["Magpie", "Kookaburra", "Cockatoo", "Galah"], answer: 1 },
    { question: "What colour is a galah?", options: ["Green and red", "Blue and yellow", "Pink and grey", "Black and white"], answer: 2 },
    { question: "Which sea creature is called a 'sea cow'?", options: ["Dolphin", "Dugong", "Whale", "Seal"], answer: 1 },
    { question: "How fast can a kangaroo hop?", options: ["20 km/h", "40 km/h", "60 km/h", "80 km/h"], answer: 2 },
    { question: "Which Australian animal has a venomous spur?", options: ["Koala", "Platypus", "Wombat", "Echidna"], answer: 1 },
    { question: "What is Australia's largest bird?", options: ["Cassowary", "Pelican", "Emu", "Wedge-tailed Eagle"], answer: 2 },
];

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
}

export default function QuizRaceGame() {
    const { api } = useAuth();
    const [started, setStarted] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [current, setCurrent] = useState(0);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(10);
    const [totalTime, setTotalTime] = useState(0);
    const [selected, setSelected] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [result, setResult] = useState(null);
    const timerRef = useRef(null);

    const startGame = () => {
        setQuestions(shuffle(QUESTIONS).slice(0, 10));
        setCurrent(0);
        setScore(0);
        setTotalTime(0);
        setSelected(null);
        setShowResult(false);
        setGameOver(false);
        setResult(null);
        setTimeLeft(10);
        setStarted(true);
    };

    useEffect(() => {
        if (!started || gameOver) return;
        timerRef.current = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) { handleTimeout(); return 10; }
                return t - 1;
            });
            setTotalTime(t => t + 1);
        }, 1000);
        return () => clearInterval(timerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [started, gameOver, current]);

    const handleTimeout = () => {
        setShowResult(true);
        setTimeout(() => nextQuestion(), 1500);
    };

    const handleAnswer = (idx) => {
        if (selected !== null || showResult) return;
        setSelected(idx);
        const correct = idx === questions[current].answer;
        if (correct) {
            setScore(s => s + Math.max(1, timeLeft)); // More time left = more points
        }
        setShowResult(true);
        setTimeout(() => nextQuestion(), 1500);
    };

    const nextQuestion = () => {
        clearInterval(timerRef.current);
        if (current + 1 >= questions.length) {
            setGameOver(true);
            completeGame();
        } else {
            setCurrent(c => c + 1);
            setSelected(null);
            setShowResult(false);
            setTimeLeft(10);
        }
    };

    const completeGame = async () => {
        const finalScore = Math.round((score / (questions.length * 10)) * 100);
        try {
            const res = await api().post('/games/complete', {
                game_id: 'quiz_race', score: finalScore, time_seconds: totalTime, difficulty: 'medium'
            });
            setResult(res.data);
        } catch (err) { console.error('Failed to submit'); }
    };

    if (!started) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-20">
                            <Link to="/games" className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-[#f59e0b] border-4 border-[#1e293b] flex items-center justify-center">
                                    <Lightning weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-[#1e293b]">Quiz Race</span>
                            </Link>
                            <Link to="/games" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5">
                                <ArrowLeft weight="bold" size={16} /> Games
                            </Link>
                        </div>
                    </div>
                </nav>
                <div className="max-w-2xl mx-auto px-4 pt-28 pb-24 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-6xl mb-4">⚡</div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b] mb-2">Animal Quiz Race</h1>
                        <p className="text-[#475569] mb-2">Answer 10 questions about Australian animals.</p>
                        <p className="text-[#475569] mb-8">You have <strong>10 seconds</strong> per question. Faster answers = more points!</p>
                        <motion.button onClick={startGame} whileHover={{ y: -4, x: -4 }} whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn bg-[#f59e0b] text-white text-lg flex items-center gap-2 mx-auto" data-testid="start-quiz">
                            <Play weight="bold" size={22} /> Start Quiz
                        </motion.button>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (gameOver) {
        const pct = Math.round((score / (questions.length * 10)) * 100);
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="brutalist-card p-8 max-w-md w-full text-center bg-white" data-testid="game-complete">
                    <div className="text-6xl mb-4">{pct >= 70 ? '🏆' : pct >= 40 ? '👍' : '💪'}</div>
                    <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Quiz Complete!</h2>
                    <p className="text-[#475569] mb-6">Score: {score} points ({pct}%)</p>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        <div className="p-3 bg-[#f8fafc] rounded-xl">
                            <Timer weight="duotone" size={20} className="text-[#a855f7] mx-auto" />
                            <p className="font-fredoka text-xl font-bold">{totalTime}s</p>
                            <p className="text-xs text-[#94a3b8]">Total Time</p>
                        </div>
                        <div className="p-3 bg-[#fef3c7] rounded-xl">
                            <Coins weight="duotone" size={20} className="text-[#f59e0b] mx-auto" />
                            <p className="font-fredoka text-xl font-bold">{result ? `+${result.tokens_earned}` : '...'}</p>
                            <p className="text-xs text-[#94a3b8]">Tokens</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={startGame} className="brutalist-btn flex-1 bg-[#f59e0b] text-white flex items-center justify-center gap-2">
                            <Play weight="bold" size={18} /> Again
                        </button>
                        <Link to="/games" className="brutalist-btn flex-1 bg-white text-[#1e293b] text-center">Games Hub</Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    const q = questions[current];
    const correctAnswer = q.answer;

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center p-4">
            <div className="max-w-lg w-full">
                {/* Progress */}
                <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-[#475569]">{current + 1}/{questions.length}</span>
                    <span className="text-sm font-bold text-[#f59e0b]">Score: {score}</span>
                </div>
                <div className="w-full h-2 bg-[#e2e8f0] rounded-full mb-4">
                    <div className="h-full bg-[#22c55e] rounded-full transition-all" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
                </div>

                {/* Timer */}
                <div className="flex justify-center mb-6">
                    <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center font-fredoka text-2xl font-bold ${timeLeft <= 3 ? 'border-[#ef4444] text-[#ef4444] animate-pulse' : 'border-[#1e293b] text-[#1e293b]'}`}>
                        {timeLeft}
                    </div>
                </div>

                {/* Question */}
                <motion.div key={current} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
                    className="brutalist-card p-6 bg-white mb-4">
                    <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-6" data-testid="question-text">{q.question}</h2>
                    <div className="space-y-3">
                        {q.options.map((opt, idx) => {
                            let bg = 'bg-white hover:bg-[#f8fafc]';
                            if (showResult) {
                                if (idx === correctAnswer) bg = 'bg-[#22c55e]/20 border-[#22c55e]';
                                else if (idx === selected && idx !== correctAnswer) bg = 'bg-[#ef4444]/20 border-[#ef4444]';
                            } else if (idx === selected) {
                                bg = 'bg-[#38bdf8]/20';
                            }
                            return (
                                <button key={idx} onClick={() => handleAnswer(idx)} disabled={showResult}
                                    className={`w-full text-left p-4 rounded-xl border-2 border-[#1e293b] font-semibold transition-colors ${bg}`}
                                    data-testid={`option-${idx}`}>
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
